import { useState } from 'react';
import { DJProfile } from '../../../lib/supabase';

interface HeroProps {
  profile: DJProfile | null;
  imageVersion?: number;
}

export default function Hero({ profile }: HeroProps) {
  const [bioExpanded, setBioExpanded] = useState(false);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const heroImage = (profile as any)?.hero_image;
  const profileImage = profile?.profile_image;

  const heroImageUrl = heroImage || null;

  const profileImageUrl = profileImage
    ? profileImage
    : `https://readdy.ai/api/search-image?query=professional%20dj%20performing%20at%20night%20club%20with%20turntables%20and%20mixing%20equipment%2C%20dynamic%20lighting%2C%20energetic%20atmosphere%2C%20modern%20electronic%20music%20scene%2C%20vibrant%20colors%2C%20professional%20photography%20style&width=800&height=1000&seq=dj-profile-001&orientation=portrait`;

  const bioText = profile?.bio || 'DJ profesional con más de 10 años de experiencia en eventos corporativos, bodas y fiestas privadas.';

  return (
    <section className="relative min-h-screen bg-black flex items-center overflow-hidden pt-20 md:pt-0">
      {heroImageUrl ? (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${heroImageUrl})` }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/70"></div>
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-black to-fuchsia-500/10"></div>
      )}

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3 space-y-8">
            <div className="space-y-4">
              <div className="inline-block">
                <span className="text-cyan-400 text-sm uppercase tracking-[0.3em] font-light">
                  {(profile as any)?.hero_subtitle || 'DJ Profesional'}
                </span>
              </div>
              <h1 className="text-6xl lg:text-8xl font-black text-white leading-none tracking-tight">
                {profile?.name || 'Miki Buades'}
              </h1>

              {/* Desktop: full bio */}
              <p className="hidden md:block text-xl lg:text-2xl text-gray-400 font-light max-w-2xl leading-relaxed">
                {bioText}
              </p>

              {/* Mobile: truncated bio with "leer más" */}
              <div className="md:hidden">
                <p
                  className={`text-lg text-gray-400 font-light leading-relaxed ${
                    !bioExpanded ? 'line-clamp-4' : ''
                  }`}
                >
                  {bioText}
                </p>
                {bioText.length > 120 && (
                  <button
                    onClick={() => setBioExpanded(!bioExpanded)}
                    className="text-cyan-400 text-sm mt-2 cursor-pointer whitespace-nowrap"
                  >
                    {bioExpanded ? 'Ver menos' : 'Leer más...'}
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-3">
              <button
                onClick={scrollToContact}
                className="group relative px-8 py-4 bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white font-bold uppercase text-sm tracking-wider rounded-full overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_rgba(6,182,212,0.6)] whitespace-nowrap cursor-pointer"
              >
                <span className="relative z-10 flex items-center justify-center gap-3">
                  <i className="ri-calendar-line text-lg hidden md:inline"></i>
                  {(profile as any)?.hero_btn_primary || 'Reservar Ahora'}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-fuchsia-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>

              <button
                onClick={() => document.getElementById('events')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 border-2 border-cyan-400 text-cyan-400 font-bold uppercase text-sm tracking-wider rounded-full hover:bg-cyan-400 hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {(profile as any)?.hero_btn_secondary || 'Ver Eventos'}
              </button>
            </div>
          </div>

          <div className="lg:col-span-2 relative">
            <div className="relative aspect-[3/4] rounded-3xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-10"></div>
              <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/60 z-10"></div>
              <img
                key={profileImageUrl}
                src={profileImageUrl}
                alt={profile?.name || 'DJ'}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 z-20 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-fuchsia-400 to-transparent animate-pulse"></div>
              </div>
            </div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-cyan-500 to-fuchsia-500 rounded-full blur-3xl opacity-50 animate-pulse"></div>
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-br from-fuchsia-500 to-cyan-500 rounded-full blur-3xl opacity-50 animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Flecha de scroll: oculta en mobile */}
      <div className="hidden md:block absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce">
        <i className="ri-arrow-down-line text-3xl text-cyan-400"></i>
      </div>
    </section>
  );
}
